'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Minus,
  Plus,
  RotateCcw,
  RotateCw,
  X
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle
} from '@/components/ui/drawer';
import { API_ENDPOINTS } from '@/lib/api/config';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import { useImageZoomPan } from '@/hooks/use-image-zoom-pan';
import type { BatchPageRecord } from '../types';

interface QrPagePreviewDrawerProps {
  batchId: number;
  // The grid's currently filtered/ordered page list, so prev/next walks the
  // same sequence the operator sees on screen.
  pages: BatchPageRecord[];
  pageId: number | null;
  onOpenChange: (pageId: number | null) => void;
  onRotatePage?: (pageId: number, delta: number) => void;
  isRotating?: boolean;
  // Cache-buster map — bumped after a server-side rotation so the freshly
  // rotated bytes are fetched instead of the stale cached blob.
  imageVersions: Record<number, number>;
}

export function QrPagePreviewDrawer({
  batchId,
  pages,
  pageId,
  onOpenChange,
  onRotatePage,
  isRotating = false,
  imageVersions
}: QrPagePreviewDrawerProps) {
  const t = useTranslations('ocr');
  const tScan = useTranslations('photoScanning');
  // Same zoom/pan hook the qr-review image viewer uses — dragging to pan
  // only kicks in once zoomLevel > 100, matching that component's behavior.
  const {
    zoomLevel,
    panOffset,
    isDragging,
    zoomIn,
    zoomOut,
    reset,
    pointerHandlers
  } = useImageZoomPan();
  // Optimistic client-side rotation per page — reset whenever the previewed
  // page changes. The server persists the real rotation; once the new blob
  // arrives (via the version bump) this optimistic overlay is redundant but
  // harmless since it's reset to 0 on the next page switch.
  const [rotation, setRotation] = useState(0);

  const open = pageId !== null;
  const index = useMemo(
    () => pages.findIndex((p) => p.id === pageId),
    [pages, pageId]
  );
  const page = index >= 0 ? pages[index] : null;
  const total = pages.length;
  const position = index + 1;
  const hasPrev = index > 0;
  const hasNext = index >= 0 && index < total - 1;

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset when the previewed page changes
    setRotation(0);
  }, [pageId, reset]);

  const canZoomIn = zoomLevel < 300;
  const canZoomOut = zoomLevel > 25;

  const canRotate = Boolean(page && onRotatePage);
  const handleRotate = (delta: number) => {
    if (!page) return;
    setRotation((prev) => (((prev + delta) % 360) + 360) % 360);
    onRotatePage?.(page.id, delta);
  };

  const goTo = (nextIndex: number) => {
    const target = pages[nextIndex];
    if (target) onOpenChange(target.id);
  };

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onOpenChange(null)}>
      <DrawerContent
        className='h-[90vh]'
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' && hasPrev) {
            e.preventDefault();
            goTo(index - 1);
          } else if (e.key === 'ArrowRight' && hasNext) {
            e.preventDefault();
            goTo(index + 1);
          }
        }}
      >
        <DrawerHeader className='flex-row items-start justify-between border-b py-3'>
          <div>
            <DrawerTitle>
              {page ? tScan('pageLabel', { n: page.sequence }) : ''}
            </DrawerTitle>
            <DrawerDescription>
              {t('rearrangePagesPreviewPosition', { position, total })}
            </DrawerDescription>
          </div>
          <DrawerClose asChild>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              aria-label={tScan('closeThumbnails')}
            >
              <X className='h-4 w-4' />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className='relative flex flex-1 items-center justify-center overflow-hidden bg-black/80 px-12'>
          {page && (
            <PreviewImage
              batchId={batchId}
              pageId={page.id}
              version={imageVersions[page.id] ?? 0}
              zoomLevel={zoomLevel}
              panOffset={panOffset}
              isDragging={isDragging}
              pointerHandlers={pointerHandlers}
              rotation={rotation}
            />
          )}

          <Button
            type='button'
            variant='secondary'
            size='icon'
            aria-label={t('rearrangePagesPreviewPrev')}
            onClick={() => goTo(index - 1)}
            disabled={!hasPrev}
            className='absolute start-3 top-1/2 -translate-y-1/2 rounded-full shadow-md'
          >
            <ChevronLeft className='h-5 w-5 rtl:hidden' />
            <ChevronRight className='hidden h-5 w-5 rtl:inline' />
          </Button>

          <Button
            type='button'
            variant='secondary'
            size='icon'
            aria-label={t('rearrangePagesPreviewNext')}
            onClick={() => goTo(index + 1)}
            disabled={!hasNext}
            className='absolute end-3 top-1/2 -translate-y-1/2 rounded-full shadow-md'
          >
            <ChevronRight className='h-5 w-5 rtl:hidden' />
            <ChevronLeft className='hidden h-5 w-5 rtl:inline' />
          </Button>

          {/* Bottom toolbar — zoom + rotate, same pill styling as the main
              document viewer's toolbar. */}
          <div className='absolute inset-x-0 bottom-0 z-10 flex justify-center pb-4'>
            <div className='bg-background/90 flex items-center gap-1 rounded-lg border px-2 py-1 shadow-md backdrop-blur-sm'>
              <Button
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                onClick={zoomOut}
                disabled={!canZoomOut}
                aria-label={t('zoomOut')}
              >
                <Minus className='h-3.5 w-3.5' />
              </Button>
              <span className='min-w-[3rem] text-center text-xs font-medium tabular-nums'>
                {zoomLevel}%
              </span>
              <Button
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                onClick={zoomIn}
                disabled={!canZoomIn}
                aria-label={t('zoomIn')}
              >
                <Plus className='h-3.5 w-3.5' />
              </Button>

              <Separator orientation='vertical' className='mx-1 h-5' />

              <Button
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                onClick={() => handleRotate(-90)}
                disabled={isRotating || !canRotate}
                aria-label={t('rotate')}
              >
                {isRotating ? (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                ) : (
                  <RotateCcw className='h-3.5 w-3.5' />
                )}
              </Button>
              <Button
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                onClick={() => handleRotate(90)}
                disabled={isRotating || !canRotate}
                aria-label={t('rotate')}
              >
                {isRotating ? (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                ) : (
                  <RotateCw className='h-3.5 w-3.5' />
                )}
              </Button>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function PreviewImage({
  batchId,
  pageId,
  version,
  zoomLevel,
  panOffset,
  isDragging,
  pointerHandlers,
  rotation
}: {
  batchId: number;
  pageId: number;
  version: number;
  zoomLevel: number;
  panOffset: { x: number; y: number };
  isDragging: boolean;
  pointerHandlers: {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: () => void;
    onPointerLeave: () => void;
  };
  rotation: number;
}) {
  const endpoint = API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(batchId, pageId);
  const authEndpoint = version > 0 ? `${endpoint}?v=${version}` : endpoint;
  const blobUrl = useAuthenticatedBlobUrl(authEndpoint);

  if (!blobUrl) {
    return (
      <div className='flex h-full items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-white' />
      </div>
    );
  }

  return (
    <div
      // Excluded from vaul's swipe-to-dismiss only while actually pannable
      // (zoomed in) — otherwise the drawer's own drag-to-close would fight
      // with panning the image. At 100% zoom there's nothing to pan, so the
      // drag gesture is left free to dismiss the drawer as normal.
      {...(zoomLevel > 100 ? { 'data-vaul-no-drag': '' } : {})}
      className={cn(
        'relative h-full w-full',
        zoomLevel > 100
          ? isDragging
            ? 'cursor-grabbing'
            : 'cursor-grab'
          : 'cursor-default'
      )}
      onPointerDown={pointerHandlers.onPointerDown}
      onPointerMove={pointerHandlers.onPointerMove}
      onPointerUp={pointerHandlers.onPointerUp}
      onPointerLeave={pointerHandlers.onPointerLeave}
    >
      <div
        className='absolute inset-0 flex items-center justify-center py-6'
        style={{ transform: `translate(${panOffset.x}px, ${panOffset.y}px)` }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- in-memory blob URL preview; next/image cannot proxy blob:// schemes */}
        <img
          src={blobUrl}
          alt=''
          draggable={false}
          style={{
            maxHeight: '100%',
            maxWidth: '100%',
            width: 'auto',
            height: 'auto',
            transform: `scale(${zoomLevel / 100}) rotate(${rotation}deg)`,
            transformOrigin: 'center',
            transition: isDragging ? 'none' : 'transform 0.15s ease'
          }}
        />
      </div>
    </div>
  );
}
